"use client";
import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import Link from "next/link";
import Image from "next/image";
import { fetchBlogPosts } from "@/app/services/blogApi";
import { BlogPost } from "@/app/types/blog";

interface CarouselPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  image: string;
  date: string;
  author: string;
  category: string;
  readTime: string;
}

const BlogsSection: React.FC = () => {
  const [items, setItems] = useState<CarouselPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const fetchBlogs = async () => {
      try {
        const { posts } = await fetchBlogPosts({ perPage: 12 });

        if (!isMounted) return;

        const formattedPosts: CarouselPost[] = posts.map((post: BlogPost) => ({
          id: post.id,
          slug: post.slug,
          title: post.title,
          excerpt: post.excerpt,
          image:
            post.image ||
            "https://placehold.co/420x200/0b1d51/ffffff?text=Temok+Blog",
          date: new Date(post.publishedAt).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          }),
          author:
            typeof post.author === "object"
              ? post.author.name
              : post.author || "Unknown Author",
          category: post.category || "General",
          readTime: post.readTime || "3 min read",
        }));

        setItems(formattedPosts);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBlogs();

    return () => {
      isMounted = false; // Cleanup to avoid setting state on unmounted component
    };
  }, []);

  return (
    <section id="blogsection" className="container-lg py-5 text-white">
      <div className="Haddings">
        <h4 className="fw-bold display-5 text-center textHad">
          Latest from our Blogs
        </h4>
      </div>
      <div className="row g-4 mt-0">
        {loading ? (
          <p>Loading blog posts...</p>
        ) : items && items.length > 0 ? (
          <Swiper
            className="mt-4 px-4"
            modules={[Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            autoplay={{ delay: 5000 }}
            breakpoints={{
              576: {
                slidesPerView: 3,
              },
              768: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 4,
              },
            }}
          >
            {items.map((item, i) => (
              <SwiperSlide key={i} className="py-3">
                <div className="col-12 col-md-12">
                  <div className="blogBord hvr-sink">
                    <div className="d-flex justify-content-between">
                      <p className="m-0 small blogDate fw-bold py-2 px-3 rounded-pill mb-3  ">
                        <span className="">{item.date}</span>
                      </p>
                    </div>
                    <Link href={`/blog/${item.slug}`} className="blog-title">
                      <Image
                        width={420}
                        height={200}
                        src={item.image}
                        className="blog-thumbnail rounded-4 img-fluid"
                        alt={item.title}
                      />
                    </Link>

                    <h4 className="textOrange fs-5 fw-semibold my-lg-4 my-md-3 my-3">
                      <Link
                        href={`/blog/${item.slug}`}
                        className="blog-title text-decoration-none textOrange"
                      >
                        {item.title}
                      </Link>
                    </h4>
                    <p className="mt-2 textPara fs-6 fw-600 line-clamp">
                      {item.excerpt.length > 180
                        ? `${item.excerpt.slice(0, 177)}...`
                        : item.excerpt}
                    </p>
                    <div className="mt-2">
                      <p className="text-muted small my-2">
                        Posted in <strong>{item.category}</strong>
                      </p>
                      <p className="text-muted small mb-0">
                        {item.readTime} • {item.author}
                      </p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <h6 className="display-7 text-center textHad">
            No blog posts found.
          </h6>
        )}
      </div>
    </section>
  );
};

export default BlogsSection;
